import api from './api';

/**
 * Builder do livro customizado. Tudo passa pelo NOSSO backend
 * (`/livro-customizado/*`), que guarda a chave do parceiro do Hero Book. O
 * navegador só conhece a `draftKey` do próprio rascunho (header `x-draft-key`).
 * As imagens do pack vêm direto do Hero Book (`<img>`, GET público).
 */

export type Presentation = 'masculine' | 'feminine' | 'neutral';
export type Trait = 'skin' | 'hairStyle' | 'hairColor' | 'eyes' | 'outfit' | 'shoes' | 'accessory';
export type Tracos = Record<Trait, string>;

export interface LivroHeroBook {
  bookId: string;
  title: string;
  description: string;
  pageCount: number;
  format?: string;
  finish?: string;
  assets: { packId: string; version: string };
}

export interface VarianteBuilder {
  id: string;
  label: string;
  swatch: string | null;
  presentations: Presentation[];
  preview: string | null;
  previewPorCor?: Record<string, string>;
  thumb: string | null;
  pigmented?: boolean;
}

export interface ManifestoBuilder {
  packId: string;
  version: string;
  assetBase: string;
  canvas: [number, number];
  ordemCamadas: string[];
  camadas: { id: string; trait: string; label: string }[];
  variantes: Record<string, VarianteBuilder[]>;
  compatibilidade: Record<string, string[]>;
  padroes: Record<string, Record<string, string>>;
}

export interface Crianca {
  displayName: string;
  grammaticalGender: 'm' | 'f' | 'name_only';
  dedication?: string;
  senderName?: string;
}

const comChave = (draftKey: string) => ({
  headers: { 'x-draft-key': draftKey },
});

// A API responde no envelope { success, data } (TransformResponseInterceptor).
export const LivroCustomizadoService = {
  async livros(): Promise<LivroHeroBook[]> {
    const res = await api.get('/livro-customizado/livros');
    return res.data.data;
  },

  async manifesto(packId: string, version: string): Promise<ManifestoBuilder> {
    const res = await api.get(`/livro-customizado/packs/${encodeURIComponent(packId)}/${encodeURIComponent(version)}`, {
      timeout: 30000,
    });
    return res.data.data;
  },

  async criarPersonagem(
    bookId: string,
    presentation: Presentation,
    turnstileToken?: string,
  ): Promise<{
    draftId: string;
    draftKey: string;
    character: Tracos & { presentation: Presentation };
  }> {
    const res = await api.post('/livro-customizado/personagens', {
      bookId,
      presentation,
      turnstileToken,
    });
    return res.data.data;
  },

  async gravarTracos(
    draftId: string,
    draftKey: string,
    tracos: Partial<Tracos> & { presentation?: Presentation },
  ): Promise<{ character: Tracos & { presentation: Presentation } }> {
    const res = await api.patch(`/livro-customizado/personagens/${draftId}`, tracos, comChave(draftKey));
    return res.data.data;
  },

  async gravarCrianca(draftId: string, draftKey: string, crianca: Crianca): Promise<void> {
    await api.patch(`/livro-customizado/rascunhos/${draftId}`, crianca, comChave(draftKey));
  },

  async previa(draftId: string, draftKey: string): Promise<number[]> {
    // A prévia é gerada dentro do GET no Hero Book.
    const res = await api.get(`/livro-customizado/personagens/${draftId}/previa`, {
      ...comChave(draftKey),
      timeout: 60000,
    });
    return res.data.data.paginas;
  },

  /** SVG da página como blob URL — `<img>` não manda header, então baixa com a chave. */
  async paginaPrevia(draftId: string, draftKey: string, indice: number): Promise<string> {
    const res = await api.get(`/livro-customizado/personagens/${draftId}/previa/${indice}`, {
      ...comChave(draftKey),
      responseType: 'blob',
      timeout: 60000,
    });
    return URL.createObjectURL(new Blob([res.data], { type: 'image/svg+xml' }));
  },
};

export function urlDoAsset(manifesto: ManifestoBuilder, caminho: string | null | undefined): string | null {
  return caminho ? `${manifesto.assetBase}${encodeURIComponent(caminho)}` : null;
}
